// Clojure course content — Part 5: "More Essentials" + a "What's Next" preview.

export const lessonsPart5 = [
  {
    id: "if-let",
    title: "if-let & when-let",
    group: "More Essentials",
    summary: "Bind a value and test it in a single, tidy step.",
    content: [
      { t: "p", text: "A very common pattern is: compute something, and if it's there (truthy), use it. Written by hand that means a let plus an if that repeats the name. if-let does both at once." },
      { t: "code", code: "(if-let [user (get {:name \"Ada\"} :name)]\n  (str \"Hello, \" user)\n  \"No user found\")" },
      { t: "p", text: "if-let binds the name, then checks whether that value is truthy. If it is, the first branch runs with the name in scope; otherwise the else branch runs (and the name is NOT available there)." },
      { t: "code", code: "(if-let [found (get {:name \"Ada\"} :email)]\n  (str \"Email: \" found)\n  \"No email on file\")" },
      { t: "h", text: "when-let" },
      { t: "p", text: "when-let is the same idea without an else branch — it runs the body only when the value is truthy, otherwise returns nil. Great for 'do this only if we actually have something'." },
      { t: "code", code: "(when-let [items (seq [])]\n  (str \"First item: \" (first items)))" },
      { t: "note", text: "Remember Clojure's truthiness: only false and nil are falsey. So if-let/when-let treat a value like 0 or \"\" as present." },
    ],
    exercises: [
      {
        prompt: "Write `greet-user` that takes a map and, if it has a :name, returns \"Hi, NAME\"; otherwise returns \"Hi, stranger\". Use if-let.",
        starter: "(defn greet-user [m]\n  )",
        tests: ["(= (greet-user {:name \"Ada\"}) \"Hi, Ada\")", "(= (greet-user {}) \"Hi, stranger\")"],
        solution: "(defn greet-user [m]\n  (if-let [n (:name m)]\n    (str \"Hi, \" n)\n    \"Hi, stranger\"))",
        hint: "(if-let [n (:name m)] (str \"Hi, \" n) \"Hi, stranger\").",
      },
      {
        prompt: "Write `first-or-nil` that returns the first element of a collection only when it is non-empty, otherwise nil. Use when-let with seq.",
        starter: "(defn first-or-nil [coll]\n  )",
        tests: ["(= (first-or-nil [10 20]) 10)", "(= (first-or-nil []) nil)", "(= (first-or-nil nil) nil)"],
        solution: "(defn first-or-nil [coll]\n  (when-let [s (seq coll)]\n    (first s)))",
        hint: "(when-let [s (seq coll)] (first s)) — seq returns nil for empty collections.",
      },
    ],
  },

  {
    id: "case",
    title: "case: Matching Values",
    group: "More Essentials",
    summary: "A clean, fast way to branch on a specific value.",
    content: [
      { t: "p", text: "You've met cond for a chain of conditions. When you're simply comparing one value against several known options, case is clearer and faster. It matches the value against each branch's constant." },
      { t: "code", code: "(defn day-type [day]\n  (case day\n    :sat \"weekend\"\n    :sun \"weekend\"\n    \"weekday\"))\n\n(day-type :sat)" },
      { t: "p", text: "Each pair is 'constant then result'. The final lone value (here \"weekday\") is the default, used when nothing matches. Unlike cond, case tests for equality with literal constants — you don't write the comparisons yourself." },
      { t: "code", code: "(defn describe-number [n]\n  (case n\n    0 \"zero\"\n    1 \"one\"\n    2 \"two\"\n    \"many\"))\n\n[(describe-number 1) (describe-number 7)]" },
      { t: "note", text: "Important: case constants must be compile-time literals (numbers, keywords, strings, symbols) — not expressions. If you need to test ranges or call functions, use cond instead. Also, without a default, an unmatched value throws an error." },
    ],
    exercises: [
      {
        prompt: "Write `traffic` that returns an action for a light: :red => \"stop\", :yellow => \"slow\", :green => \"go\", anything else => \"unknown\". Use case.",
        starter: "(defn traffic [light]\n  )",
        tests: ["(= (traffic :red) \"stop\")", "(= (traffic :green) \"go\")", "(= (traffic :blue) \"unknown\")"],
        solution: "(defn traffic [light]\n  (case light\n    :red \"stop\"\n    :yellow \"slow\"\n    :green \"go\"\n    \"unknown\"))",
        hint: "Use (case light ...) with pairs like :red \"stop\", then a final default \"unknown\".",
      },
      {
        prompt: "Write `coin-value` returning cents for a coin keyword: :penny 1, :nickel 5, :dime 10, :quarter 25 (default 0). Use case.",
        starter: "(defn coin-value [coin]\n  )",
        tests: ["(= (coin-value :dime) 10)", "(= (coin-value :quarter) 25)", "(= (coin-value :doubloon) 0)"],
        solution: "(defn coin-value [coin]\n  (case coin\n    :penny 1\n    :nickel 5\n    :dime 10\n    :quarter 25\n    0))",
        hint: "Pairs of keyword then number, with a final default 0.",
      },
    ],
  },

  {
    id: "for",
    title: "for: List Comprehensions",
    group: "More Essentials",
    summary: "Build a new sequence by looping over one or more collections.",
    content: [
      { t: "p", text: "for builds a sequence by walking over collections and collecting a result for each combination. Don't confuse it with loops in other languages — Clojure's for produces a value (a lazy sequence), it doesn't just repeat actions (that's doseq)." },
      { t: "code", code: "(for [x [1 2 3 4]]\n  (* x x))" },
      { t: "p", text: "That reads: for each x in the vector, collect (* x x). You can filter as you go with :when, keeping only elements that pass a test." },
      { t: "code", code: "(for [x (range 10)\n      :when (even? x)]\n  x)" },
      { t: "h", text: "Multiple bindings" },
      { t: "p", text: "With more than one binding, for produces every combination — like nested loops. This is handy for building pairs or grids." },
      { t: "code", code: "(for [suit [:hearts :spades]\n      rank [1 2 3]]\n  [suit rank])" },
      { t: "note", text: "for is for producing data; doseq (which looks almost identical) is for side effects. If you want a list of results, reach for for." },
    ],
    exercises: [
      {
        prompt: "Write `cubes` that returns a sequence of the cubes (x*x*x) of the numbers in a collection. e.g. (cubes [1 2 3]) => (1 8 27).",
        starter: "(defn cubes [nums]\n  )",
        tests: ["(= (cubes [1 2 3]) '(1 8 27))", "(= (cubes [4]) '(64))"],
        solution: "(defn cubes [nums]\n  (for [x nums]\n    (* x x x)))",
        hint: "(for [x nums] (* x x x)).",
      },
      {
        prompt: "Write `evens-doubled` that, using for with :when, returns each even number in a collection doubled. e.g. (evens-doubled [1 2 3 4]) => (4 8).",
        starter: "(defn evens-doubled [nums]\n  )",
        tests: ["(= (evens-doubled [1 2 3 4]) '(4 8))", "(= (evens-doubled [1 3 5]) '())"],
        solution: "(defn evens-doubled [nums]\n  (for [x nums\n        :when (even? x)]\n    (* x 2)))",
        hint: "Use (for [x nums :when (even? x)] (* x 2)).",
      },
    ],
  },

  {
    id: "nested-maps",
    title: "Nested Data: get-in & update-in",
    group: "More Essentials",
    summary: "Reach into and update maps inside maps.",
    content: [
      { t: "p", text: "Real data is often nested — maps inside maps inside vectors. Digging in with repeated get calls is clumsy. Clojure gives you path-based tools that take a vector of keys describing the route to the value." },
      { t: "code", code: "(def data {:user {:name \"Ada\" :address {:city \"London\"}}})\n\n(get-in data [:user :address :city])" },
      { t: "p", text: "get-in follows the path [:user :address :city] and returns the value at the end (or nil if any step is missing). It's the nested version of get." },
      { t: "h", text: "assoc-in & update-in" },
      { t: "p", text: "assoc-in sets a value deep inside, creating maps along the way if needed. It returns a NEW nested map — the original is untouched." },
      { t: "code", code: "(assoc-in {:user {:name \"Ada\"}}\n          [:user :age]\n          36)" },
      { t: "p", text: "update-in applies a function to the value at a path — perfect for incrementing a deep counter or transforming a nested value." },
      { t: "code", code: "(def scores {:players {:ada {:points 10}}})\n\n(update-in scores [:players :ada :points] + 5)" },
      { t: "note", text: "These path tools keep your code flat and readable no matter how deep the data goes — and, like everything in Clojure, they return new data rather than mutating the old." },
    ],
    exercises: [
      {
        prompt: "Write `city-of` that returns the city from a map shaped like {:user {:address {:city \"…\"}}}. Use get-in.",
        starter: "(defn city-of [m]\n  )",
        tests: ["(= (city-of {:user {:address {:city \"London\"}}}) \"London\")", "(= (city-of {:user {}}) nil)"],
        solution: "(defn city-of [m]\n  (get-in m [:user :address :city]))",
        hint: "(get-in m [:user :address :city]).",
      },
      {
        prompt: "Write `add-point` that adds 1 to the value at [:players :ada :points] in the given map. Use update-in.",
        starter: "(defn add-point [m]\n  )",
        tests: ["(= (add-point {:players {:ada {:points 3}}}) {:players {:ada {:points 4}}})", "(= (get-in (add-point {:players {:ada {:points 0}}}) [:players :ada :points]) 1)"],
        solution: "(defn add-point [m]\n  (update-in m [:players :ada :points] inc))",
        hint: "(update-in m [:players :ada :points] inc).",
      },
    ],
  },

  {
    id: "some-every",
    title: "some & every?",
    group: "More Essentials",
    summary: "Ask questions about a whole collection at once.",
    content: [
      { t: "p", text: "Often you don't want to transform a collection — you just want to ask a yes/no question about it. some and every? are made for this." },
      { t: "p", text: "every? returns true only if a predicate holds for EVERY element." },
      { t: "code", code: "(every? pos? [1 2 3 4])" },
      { t: "code", code: "(every? pos? [1 -2 3])" },
      { t: "h", text: "some" },
      { t: "p", text: "some returns the first truthy result of applying a predicate — effectively 'is there ANY element that matches?'. It returns the found value (or nil), not always true/false." },
      { t: "code", code: "(some even? [1 3 5 6 7])" },
      { t: "p", text: "A neat trick: because sets act as functions, (some #{target} coll) tells you whether target is in the collection." },
      { t: "code", code: "(some #{:banana} [:apple :banana :cherry])" },
      { t: "note", text: "Pair them with not: not-any? and not-every? exist too. And remember some returns the matching value, so wrap it in (boolean …) if you specifically need true/false." },
    ],
    exercises: [
      {
        prompt: "Write `all-adults?` that returns true only if every age in a collection is 18 or older.",
        starter: "(defn all-adults? [ages]\n  )",
        tests: ["(= (all-adults? [20 30 18]) true)", "(= (all-adults? [20 15]) false)", "(= (all-adults? []) true)"],
        solution: "(defn all-adults? [ages]\n  (every? #(>= % 18) ages))",
        hint: "(every? #(>= % 18) ages).",
      },
      {
        prompt: "Write `has-negative?` that returns true if ANY number in a collection is negative, else false. (Wrap some in boolean so it returns true/false.)",
        starter: "(defn has-negative? [nums]\n  )",
        tests: ["(= (has-negative? [1 2 -3]) true)", "(= (has-negative? [1 2 3]) false)"],
        solution: "(defn has-negative? [nums]\n  (boolean (some neg? nums)))",
        hint: "(boolean (some neg? nums)).",
      },
    ],
  },

  {
    id: "comp-partial",
    title: "comp & partial",
    group: "More Essentials",
    summary: "Build new functions out of existing ones.",
    content: [
      { t: "p", text: "In Clojure, functions are values you can combine. Two tools let you make new functions without writing fn every time: comp and partial." },
      { t: "h", text: "comp: chain functions together" },
      { t: "p", text: "comp composes functions right-to-left: the result of one becomes the input of the next. (comp f g) means 'apply g, then f'." },
      { t: "code", code: "(def inc-then-double\n  (comp #(* 2 %) inc))\n\n(inc-then-double 5)" },
      { t: "p", text: "That takes 5, increments to 6, then doubles to 12. It's the same as writing (fn [x] (* 2 (inc x))), but more declarative." },
      { t: "h", text: "partial: fix some arguments" },
      { t: "p", text: "partial takes a function and some of its leading arguments, and returns a new function waiting for the rest. It's a quick way to make a specialised version of a general function." },
      { t: "code", code: "(def add-ten (partial + 10))\n\n(add-ten 5)" },
      { t: "code", code: "(def triple (partial * 3))\n(map triple [1 2 3])" },
      { t: "note", text: "comp and partial shine inside map/filter/reduce, where you want to hand over a small, purpose-built function. They keep pipelines clean and point-free." },
    ],
    exercises: [
      {
        prompt: "Using comp, build `neg-inc` that increments a number and then negates it. e.g. (neg-inc 4) => -5.",
        starter: "(def neg-inc )",
        tests: ["(= (neg-inc 4) -5)", "(= (neg-inc 0) -1)"],
        solution: "(def neg-inc (comp - inc))",
        hint: "(comp - inc) — inc runs first, then - negates.",
      },
      {
        prompt: "Using partial, build `times-100` that multiplies its argument by 100.",
        starter: "(def times-100 )",
        tests: ["(= (times-100 3) 300)", "(= (times-100 0) 0)"],
        solution: "(def times-100 (partial * 100))",
        hint: "(partial * 100).",
      },
    ],
  },

  {
    id: "more-sequence-tools",
    title: "More Sequence Tools",
    group: "More Essentials",
    summary: "A grab-bag of everyday helpers: map-indexed, mapv, sort, sort-by, group-by, apply and do.",
    content: [
      { t: "p", text: "Real Clojure code mostly uses a handful of small, focused tools. This lesson collects seven more of them — each does one simple job, and together they cover a surprising amount of everyday data work." },
      { t: "h", text: "map-indexed: know where you are" },
      { t: "p", text: "map hands each element to a function. map-indexed hands each element AND its position (starting at 0) — perfect when order matters, like numbering a list." },
      { t: "code", code: "(map-indexed vector [\"red\" \"green\" \"blue\"])" },
      { t: "p", text: "The function receives the index first, then the element. Use str to build a numbered list:" },
      { t: "code", code: "(map-indexed (fn [i c] (str (inc i) \". \" c)) [\"tea\" \"coffee\"])" },
      { t: "note", text: "You'll meet map-indexed again in the Web Dev course, where it's a tidy way to give each list item a :key based on its position." },
      { t: "h", text: "mapv: map that returns a vector" },
      { t: "p", text: "map returns a lazy sequence. When you want a vector back — a concrete, indexed collection — use mapv. Same idea, vector-shaped result." },
      { t: "code", code: "(mapv inc [1 2 3])" },
      { t: "p", text: "If you already have a lazy sequence and just want it as a vector, vec does the same job in one step:" },
      { t: "code", code: "(vec (map inc [1 2 3]))" },
      { t: "note", text: "Rule of thumb: use map when the result feeds onward into another sequence function; use mapv when the result should BE a vector — say, to store it in an atom." },
      { t: "h", text: "sort and sort-by" },
      { t: "p", text: "sort orders a collection. Numbers and strings sort naturally; anything else sorts by its string form." },
      { t: "code", code: "(sort [3 1 4 1 5 9 2])" },
      { t: "code", code: "(sort [\"pear\" \"apple\" \"mango\"])" },
      { t: "p", text: "sort-by sorts using a KEY function — it compares the result of calling that function on each element. Sort products by their :price, or words by their length:" },
      { t: "code", code: "(sort-by count [\"a\" \"bbb\" \"cc\"])" },
      { t: "code", code: "(sort-by :price [{:name \"Pen\" :price 2}\n              {:name \"Cup\" :price 8}\n              {:name \"Desk\" :price 199}])" },
      { t: "p", text: "Pass a comparator like > as the second argument to flip the order:" },
      { t: "code", code: "(sort-by count > [\"a\" \"bbb\" \"cc\"])" },
      { t: "h", text: "group-by: bucket your data" },
      { t: "p", text: "group-by splits a collection into groups. Give it a key function; you get back a map from each result to a vector of the elements that produced it." },
      { t: "code", code: "(group-by even? [1 2 3 4 5 6])" },
      { t: "code", code: "(group-by :department [{:name \"Ada\" :department :engineering}\n                      {:name \"Alan\" :department :engineering}\n                      {:name \"Grace\" :department :math}])" },
      { t: "note", text: "group-by is the answer to 'how many of each?' and 'split these by category' — a one-liner where a hand-written loop would take several lines." },
      { t: "h", text: "apply: spread a collection into arguments" },
      { t: "p", text: "Some functions expect their arguments separately, but your data sits in a collection. apply calls a function using a collection's elements as the arguments." },
      { t: "code", code: "(apply + [1 2 3 4 5])" },
      { t: "p", text: "That's the same as (+ 1 2 3 4 5). It shines with max and min, which take any number of separate arguments:" },
      { t: "code", code: "(apply max [3 9 2 7])" },
      { t: "h", text: "do: run several things, keep the last" },
      { t: "p", text: "do runs a series of expressions and returns the value of the LAST one. It's how you bundle a few steps — print something, then produce a result:" },
      { t: "code", code: "(do (println \"computing...\")\n    (+ 1 2))" },
      { t: "p", text: "You rarely write do by hand, but you'll meet it again in the macros lesson, where an expansion like (if test nil body) can need to run several expressions in one branch." },
      { t: "note", text: "Seven small tools, one idea: Clojure's core is a small set of building blocks, and real programs are mostly the right combination of them." },
    ],
    exercises: [
      {
        prompt: "Write `numbered` that returns strings like \"1. tea\" for each item, using map-indexed (with the vector function) and mapv with destructuring. e.g. (numbered [\"tea\" \"coffee\"]) => [\"1. tea\" \"2. coffee\"].",
        starter: "(defn numbered [items]\n  )",
        tests: ["(= (numbered [\"tea\" \"coffee\"]) [\"1. tea\" \"2. coffee\"])", "(= (numbered [\"a\"]) [\"1. a\"])", "(= (numbered []) [])"],
        solution: "(defn numbered [items]\n  (mapv (fn [[i item]] (str (inc i) \". \" item))\n        (map-indexed vector items)))",
        hint: "(map-indexed vector items) gives ([0 \"tea\"] [1 \"coffee\"]); destructure each [i item] pair, and remember indices start at 0 so add 1.",
      },
      {
        prompt: "Write `cheapest-first` that sorts product maps by :price, cheapest first. e.g. (cheapest-first [{:name \"Desk\" :price 199} {:name \"Pen\" :price 2}]) => [{:name \"Pen\" :price 2} {:name \"Desk\" :price 199}].",
        starter: "(defn cheapest-first [products]\n  )",
        tests: ["(= (cheapest-first [{:name \"Desk\" :price 199} {:name \"Pen\" :price 2}]) [{:name \"Pen\" :price 2} {:name \"Desk\" :price 199}])", "(= (cheapest-first [{:name \"Pen\" :price 2}]) [{:name \"Pen\" :price 2}])"],
        solution: "(defn cheapest-first [products]\n  (sort-by :price products))",
        hint: "sort-by with the key :price puts the cheapest product first.",
      },
      {
        prompt: "Write `by-size` that groups a collection of strings by their length. e.g. (by-size [\"a\" \"bb\" \"c\"]) => {1 [\"a\" \"c\"] 2 [\"bb\"]}.",
        starter: "(defn by-size [words]\n  )",
        tests: ["(= (by-size [\"a\" \"bb\" \"c\"]) {1 [\"a\" \"c\"] 2 [\"bb\"]})", "(= (by-size []) {})"],
        solution: "(defn by-size [words]\n  (group-by count words))",
        hint: "group-by with count as the key function: (group-by count words).",
      },
      {
        prompt: "Write `biggest` that returns the largest number in a (non-empty) collection using apply. e.g. (biggest [3 9 2 7]) => 9.",
        starter: "(defn biggest [nums]\n  )",
        tests: ["(= (biggest [3 9 2 7]) 9)", "(= (biggest [-5 -2 -9]) -2)"],
        solution: "(defn biggest [nums]\n  (apply max nums))",
        hint: "(apply max nums) spreads the collection into max's arguments.",
      },
    ],
  },

  {
    id: "whats-next",
    title: "What's Next: Web Development with ClojureScript",
    group: "What's Next",
    summary: "Where to take Clojure from here — building real web apps.",
    content: [
      { t: "p", text: "Congratulations — you now know the core of Clojure! Everything you've learned runs as ClojureScript, the same language compiled to JavaScript. That means you can build real, interactive websites entirely in Clojure. This lesson is a preview of that world; a full follow-on course will cover it properly." },
      { t: "note", text: "The code samples below are shown for reading, not running — they need a real project build (with tools like shadow-cljs), which a browser sandbox can't provide. Treat them as a taste of what's ahead." },
      { t: "h", text: "1. HTML as data (Hiccup)" },
      { t: "p", text: "In ClojureScript you don't write HTML templates — you describe the page using plain Clojure vectors, a format called Hiccup. A keyword is the tag, a map is the attributes, and the rest are children. It's just data, so all your sequence skills apply." },
      { t: "read", code: ";; A vector describes an HTML element:\n[:div {:class \"card\"}\n  [:h1 \"Hello, Clojure!\"]\n  [:p \"You already know how to build this.\"]]" },
      { t: "h", text: "2. Components with Reagent" },
      { t: "p", text: "Reagent is a popular ClojureScript library that turns Hiccup into a live user interface (it wraps React). A component is just a function that returns Hiccup — exactly the functions you've been writing all along." },
      { t: "read", code: "(defn greeting [name]\n  [:div.greeting\n    [:h2 (str \"Hi, \" name \"!\")]\n    [:button {:on-click #(js/alert \"clicked\")}\n      \"Say hello\"]])" },
      { t: "h", text: "3. State with reactive atoms" },
      { t: "p", text: "Remember atoms? In Reagent, a special reactive atom holds your app's state, and the UI automatically re-renders whenever it changes. Your knowledge of atoms and swap! transfers directly." },
      { t: "read", code: "(defonce counter (reagent/atom 0))\n\n(defn counter-view []\n  [:div\n    [:p \"Count: \" @counter]\n    [:button {:on-click #(swap! counter inc)} \"+1\"]])" },
      { t: "h", text: "4. The bigger picture" },
      { t: "p", text: "From here, a full web course would add: managing larger apps with re-frame (events and subscriptions), talking to servers over the network (sending and receiving EDN or JSON), client-side routing (which you've actually seen a hand-rolled version of in this very app!), and finally deploying a full-stack Clojure application with shared code between the browser and server." },
      { t: "p", text: "The through-line is the thing that makes Clojure special: it's the same simple language, the same immutable data, and the same small set of functions — whether you're adding numbers in the REPL or building a production web app. You've already learned the hard part." },
      { t: "note", text: "Keep the REPL and Cheat Sheet handy, revisit exercises, and build something small of your own. That's the fastest way to make it all stick. Happy hacking!" },
      { t: "cta", text: "Ready to build real, interactive websites with everything you've learned? Your follow-on course is waiting.", label: "Start: Web Dev with ClojureScript", courseId: "webdev" },
    ],
    exercises: [],
  },
];
